import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, RotateCcw, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./primitives";
import { CornerFrame } from "./CornerFrame";
import { WaitlistModal } from "./WaitlistModal";

export const TerminalTour = () => {
  const [open, setOpen] = useState(false);
  const [ended, setEnded] = useState(false);
  const [waitlist, setWaitlist] = useState(false);
  const videoRef = useRef(null);
  const previewRef = useRef(null);

  // Autoplay the muted loop preview only while it's on screen
  useEffect(() => {
    const v = previewRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) v.play().catch(() => {}); else v.pause(); },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const openTour = () => { setEnded(false); if (previewRef.current) previewRef.current.pause(); setOpen(true); };

  const close = () => {
    if (videoRef.current) { videoRef.current.pause(); }
    setEnded(false);
    setOpen(false);
  };

  const replay = () => {
    setEnded(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const joinFromTour = () => {
    if (videoRef.current) videoRef.current.pause();
    setOpen(false);
    setEnded(false);
    setWaitlist(true);
  };

  return (
    <section id="terminal-tour" data-testid="terminal-tour" className="relative bg-[#0A0A0A] border-t border-white/[0.06] overflow-hidden">
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full bg-white/[0.03] blur-[160px]" />
      <div className="relative mx-auto max-w-[1100px] px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <SectionLabel index="//">See it in motion</SectionLabel>
          <h2 className="mt-4 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-[1.02] max-w-2xl">
            Watch the terminal, <span className="text-[#7D8390]">end to end.</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm text-white/55 leading-relaxed">
            A full guided tour of the TradrQuant terminal — screener, signals, order flow, gamma,
            liquidations and the verified ledger. No edits, no mockups. This is the software.
          </p>
        </Reveal>

        {/* poster + play */}
        <Reveal delay={0.12}>
          <button
            type="button"
            onClick={openTour}
            data-testid="terminal-tour-open"
            aria-label="Play the TradrQuant terminal tour"
            className="group relative mt-10 block w-full border border-white/[0.10] bg-black overflow-hidden shadow-[0_40px_110px_-30px_rgba(0,0,0,0.95)]"
          >
            <CornerFrame />
            {/* window chrome */}
            <div className="flex items-center gap-2 border-b border-white/[0.10] bg-[#0c0c0c] px-3.5 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/80" />
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 truncate">terminal.tradrquant — full product tour</span>
              <span className="ml-auto hidden sm:flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#BACD1C]" />
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#B4BAC4]">10 min</span>
              </span>
            </div>

            <div className="relative">
              <video
                ref={previewRef}
                src="/terminal.mp4"
                poster="/terminal-tour-poster.jpg"
                muted
                loop
                playsInline
                preload="metadata"
                data-testid="terminal-tour-preview"
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.015]"
              />
              {/* dim + play */}
              <span className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-300" />
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-4">
                <span className="relative flex items-center justify-center">
                  <span className="absolute h-20 w-20 rounded-full bg-[#BACD1C]/25 animate-ping" />
                  <span className="relative flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-[#BACD1C] text-black shadow-[0_0_30px_-8px_rgba(186,205,28,0.35)] group-hover:scale-105 transition-transform duration-300">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                </span>
                <span className="text-center font-mono text-[11px] sm:text-sm uppercase tracking-[0.15em] sm:tracking-[0.25em] text-white group-hover:text-[#BACD1C] transition-colors duration-300">
                  Click for the full guided tour
                </span>
              </span>
            </div>
          </button>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-4 font-mono text-[11px] text-white/30 tracking-[0.1em]">
            Looping preview · click for the full walkthrough with sound · educational only, not financial advice
          </p>
        </Reveal>
      </div>

      {/* lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} data-testid="terminal-tour-modal"
          >
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
            <motion.div
              className="relative w-full max-w-5xl border border-white/[0.10] bg-black overflow-hidden"
              initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 20 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 border-b border-white/[0.10] bg-[#0c0c0c] px-3.5 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/80" />
                <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 truncate">terminal.tradrquant — full product tour</span>
                <button onClick={close} data-testid="terminal-tour-close" aria-label="Close" className="ml-auto text-white/50 hover:text-[#BACD1C] transition-colors">
                  <X size={18} />
                </button>
              </div>
              <div className="relative bg-black">
                <video
                  ref={videoRef}
                  src="/terminal-tour.mp4"
                  poster="/terminal-tour-poster.jpg"
                  controls autoPlay playsInline
                  onEnded={() => setEnded(true)}
                  onPlay={() => setEnded(false)}
                  data-testid="terminal-tour-video"
                  className="w-full h-auto max-h-[80vh] bg-black block"
                />
                {/* end-of-video waitlist nudge */}
                <AnimatePresence>
                  {ended && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      data-testid="terminal-tour-endcard"
                      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 bg-black/88 backdrop-blur-sm px-6 text-center"
                    >
                      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[300px] w-[300px] rounded-full bg-[#BACD1C]/[0.12] blur-[120px]" />
                      <div className="relative">
                        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9DA3AE]">// you've seen it</div>
                        <h3 className="mt-3 font-display font-semibold tracking-[-0.03em] text-2xl sm:text-4xl leading-[1.05] text-white">
                          Now get in <span className="text-[#7D8390]">first.</span>
                        </h3>
                        <p className="mt-3 max-w-md mx-auto text-xs sm:text-sm text-white/55 leading-relaxed">
                          Create your free account to open the terminal and lock in founder pricing.
                        </p>
                      </div>
                      <div className="relative flex flex-col sm:flex-row items-center gap-3">
                        <button
                          onClick={joinFromTour}
                          data-testid="terminal-tour-join-waitlist"
                          className="group inline-flex items-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-white transition-colors duration-300"
                        >
                          Create free account
                          <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                        </button>
                        <button
                          onClick={replay}
                          data-testid="terminal-tour-replay"
                          className="inline-flex items-center gap-2 border border-white/20 text-white/80 font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 hover:border-[#BACD1C] hover:text-[#BACD1C] transition-colors duration-300"
                        >
                          <RotateCcw size={14} /> Replay
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {waitlist && <WaitlistModal open={waitlist} onClose={() => setWaitlist(false)} />}
      </AnimatePresence>
    </section>
  );
};
